/**
 * Payment Service
 *
 * Handles subscription creation, updates, and cancellation.
 * Uses Supabase Edge Functions for secure payment processing.
 * Falls back to mock functionality in mock mode.
 */

import type {
  Subscription,
  SubscriptionTier,
  BillingInterval,
  StartupTier,
  InvestorTier,
} from '../subscription/types';
import {
  getStripePriceId,
  getStartupPrice,
  getInvestorPrice,
} from '../subscription/pricing';
import { mode } from '@/lib/config/app-mode';
import {
  createCheckoutSession,
  createPortalSession,
} from '../supabase/edge-functions';

// ============================================================================
// Types
// ============================================================================

export interface CreateSubscriptionParams {
  tier: SubscriptionTier;
  billingInterval: BillingInterval;
  userRole: 'startup' | 'investor';
  customerId?: string;
}

export interface CreateSubscriptionResult {
  success: boolean;
  subscription?: Subscription;
  clientSecret?: string;
  error?: string;
}

export interface UpdateSubscriptionParams {
  subscriptionId: string;
  newTier: SubscriptionTier;
  userRole: 'startup' | 'investor';
}

export interface CancelSubscriptionParams {
  subscriptionId: string;
  cancelImmediately?: boolean;
}

export interface CustomerPortalResult {
  url: string;
}

// ============================================================================
// Mock Customer Portal URL
// ============================================================================

const MOCK_CUSTOMER_PORTAL_URL = 'https://billing.stripe.com/p/mock_portal';

// ============================================================================
// Payment Service Class
// ============================================================================

class PaymentService {
  private mockCustomerId: string | null = null;
  private mockSubscription: Subscription | null = null;

  /**
   * Create a new subscription
   */
  async createSubscription(
    params: CreateSubscriptionParams
  ): Promise<CreateSubscriptionResult> {
    const { tier, billingInterval, userRole, customerId } = params;

    // Simulate API delay
    await this.simulateDelay(800);

    // Get price ID (mock)
    const priceId = getStripePriceId(userRole, tier, billingInterval);
    if (!priceId && tier !== 'free') {
      return {
        success: false,
        error: 'Invalid tier or billing interval',
      };
    }

    // Create or use existing customer ID
    const newCustomerId = customerId || this.generateMockCustomerId();
    this.mockCustomerId = newCustomerId;

    // Calculate period dates
    const now = new Date();
    const periodEnd = new Date(now);
    if (billingInterval === 'month') {
      periodEnd.setMonth(periodEnd.getMonth() + 1);
    } else {
      periodEnd.setFullYear(periodEnd.getFullYear() + 1);
    }

    // Create subscription object
    const subscription: Subscription = {
      id: this.generateMockSubscriptionId(),
      tier,
      status: 'active',
      billingInterval,
      currentPeriodStart: now.toISOString(),
      currentPeriodEnd: periodEnd.toISOString(),
      cancelAtPeriodEnd: false,
      stripeSubscriptionId: `sub_mock_${Date.now()}`,
      stripeCustomerId: newCustomerId,
      stripePriceId: priceId ?? undefined,
    };

    this.mockSubscription = subscription;

    return {
      success: true,
      subscription,
      clientSecret: `pi_mock_${Date.now()}_secret_${Math.random().toString(36).slice(2)}`,
    };
  }

  /**
   * Update an existing subscription (upgrade/downgrade)
   */
  async updateSubscription(
    params: UpdateSubscriptionParams
  ): Promise<CreateSubscriptionResult> {
    const { subscriptionId, newTier, userRole } = params;

    await this.simulateDelay(600);

    if (!this.mockSubscription || this.mockSubscription.id !== subscriptionId) {
      return {
        success: false,
        error: 'Subscription not found',
      };
    }

    // Get new price ID
    const priceId = getStripePriceId(
      userRole,
      newTier,
      this.mockSubscription.billingInterval
    );

    // Update subscription
    this.mockSubscription = {
      ...this.mockSubscription,
      tier: newTier,
      stripePriceId: priceId ?? undefined,
      cancelAtPeriodEnd: false,
      canceledAt: undefined,
    };

    return {
      success: true,
      subscription: this.mockSubscription,
    };
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(
    params: CancelSubscriptionParams
  ): Promise<{ success: boolean; error?: string }> {
    const { subscriptionId, cancelImmediately } = params;

    await this.simulateDelay(400);

    if (!this.mockSubscription || this.mockSubscription.id !== subscriptionId) {
      return {
        success: false,
        error: 'Subscription not found',
      };
    }

    if (cancelImmediately) {
      // Immediate cancellation
      this.mockSubscription = {
        ...this.mockSubscription,
        status: 'canceled',
        canceledAt: new Date().toISOString(),
      };
    } else {
      // Cancel at period end
      this.mockSubscription = {
        ...this.mockSubscription,
        cancelAtPeriodEnd: true,
        canceledAt: new Date().toISOString(),
      };
    }

    return { success: true };
  }

  /**
   * Reactivate a canceled subscription (before period end)
   */
  async reactivateSubscription(
    subscriptionId: string
  ): Promise<{ success: boolean; error?: string }> {
    await this.simulateDelay(400);

    if (!this.mockSubscription || this.mockSubscription.id !== subscriptionId) {
      return {
        success: false,
        error: 'Subscription not found',
      };
    }

    if (this.mockSubscription.status === 'canceled') {
      return {
        success: false,
        error: 'Cannot reactivate a fully canceled subscription',
      };
    }

    this.mockSubscription = {
      ...this.mockSubscription,
      cancelAtPeriodEnd: false,
      canceledAt: undefined,
    };

    return { success: true };
  }

  /**
   * Get customer portal URL
   */
  async getCustomerPortalUrl(
    customerId?: string,
    returnUrl?: string
  ): Promise<CustomerPortalResult> {
    // Use Edge Function when not in mock mode
    if (!mode.isMock && returnUrl) {
      const { data, error } = await createPortalSession({
        returnUrl,
      });

      if (error || !data) {
        throw new Error(error || 'Failed to create portal session');
      }

      return {
        url: data.url,
      };
    }

    // Mock implementation for mock mode
    await this.simulateDelay(300);

    const cid = customerId || this.mockCustomerId;
    if (!cid) {
      throw new Error('No customer ID available');
    }

    return {
      url: `${MOCK_CUSTOMER_PORTAL_URL}?customer=${cid}`,
    };
  }

  /**
   * Create a checkout session for initial subscription
   */
  async createCheckoutSession(params: {
    tier: SubscriptionTier;
    billingInterval: BillingInterval;
    userRole: 'startup' | 'investor';
    successUrl: string;
    cancelUrl: string;
  }): Promise<{ sessionId: string; url: string }> {
    // Use Edge Function when not in mock mode
    if (!mode.isMock) {
      const tier = params.tier as 'starter' | 'standard' | 'premium';
      if (!['starter', 'standard', 'premium'].includes(tier)) {
        throw new Error('Invalid tier for checkout');
      }

      const { data, error } = await createCheckoutSession({
        tier,
        billingInterval: params.billingInterval,
        successUrl: params.successUrl,
        cancelUrl: params.cancelUrl,
      });

      if (error || !data) {
        throw new Error(error || 'Failed to create checkout session');
      }

      return {
        sessionId: data.sessionId,
        url: data.url,
      };
    }

    // Mock implementation for mock mode
    await this.simulateDelay(500);

    const sessionId = `cs_mock_${Date.now()}`;

    return {
      sessionId,
      url: `https://checkout.stripe.com/mock?session=${sessionId}`,
    };
  }

  /**
   * Get subscription price preview
   */
  getSubscriptionPrice(
    tier: SubscriptionTier,
    billingInterval: BillingInterval,
    userRole: 'startup' | 'investor'
  ): number {
    if (userRole === 'startup') {
      return getStartupPrice(tier as StartupTier, billingInterval);
    }
    return getInvestorPrice(tier as InvestorTier, billingInterval);
  }

  /**
   * Retry failed payment
   */
  async retryPayment(
    subscriptionId: string
  ): Promise<{ success: boolean; error?: string }> {
    await this.simulateDelay(1000);

    if (!this.mockSubscription || this.mockSubscription.id !== subscriptionId) {
      return {
        success: false,
        error: 'Subscription not found',
      };
    }

    // Simulate 80% success rate
    const success = Math.random() > 0.2;

    if (success) {
      this.mockSubscription = {
        ...this.mockSubscription,
        status: 'active',
      };
      return { success: true };
    }

    return {
      success: false,
      error: 'Payment failed. Please update your payment method.',
    };
  }

  // ========================================================================
  // Private Helpers
  // ========================================================================

  private simulateDelay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private generateMockCustomerId(): string {
    return `cus_mock_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private generateMockSubscriptionId(): string {
    return `sub_mock_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Reset mock state (for testing)
   */
  resetMockState(): void {
    this.mockCustomerId = null;
    this.mockSubscription = null;
  }

  /**
   * Get current mock subscription (for testing)
   */
  getMockSubscription(): Subscription | null {
    return this.mockSubscription;
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const paymentService = new PaymentService();

// Also export the class for testing
export { PaymentService };
